import React, { useEffect, useMemo, useState , useImperativeHandle, forwardRef, useRef } from "react";
import {  Modal, Select , Input , Typography , Radio  } from "antd";
import { placement_exam_logo, PlacementExamStopIcon } from "utils/assets/images";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { usePlacementExamSession } from "store/placement_exam/selectors";
import SpinLoader from "components/Loader/SpinLoader";
import { useDispatch } from "react-redux";
import {
  handleCreatePlacementExamSession,
  handleCreatePlacementExamSubmissions,
  handleUpdatePlacementExamSession,
} from "store/placement_exam/thunks";
import { authHelper } from "utils/lib/authHelper";
import { useFirebase } from "containers/FirebaseContext";
import { decodeJwtPayload } from "utils/lib/jwtHelper";
import { handleVerifyAuth } from "store/userPreference/thunks";
import { goFullScreen } from "utils";
import * as Sentry from '@sentry/react';
import { handleAwsLogin } from "utils/lib/auth";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import useTouchDetection from "components/hooks/useTouchDeviceDetection";
import SentryWrap from 'components/Sentry';

const { TextArea } = Input;
const { Title } = Typography;
const InputField = styled.input`
  color: #000;
`;

const ErrorMessage = styled.div`
  color: red;
  font-size: 12px;
  margin-top: 4px;
  border: ${(p) => (p.error ? "1px solid red" : "0px")};
`;
const WelcomeTitle = styled.h2`
  white-space: nowrap;
`;
const SchoolSelector = styled(Select)`
   width: 100%;
  .ant-select-selector {
    border-radius: 22px !important;
    min-height: 40px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`
const SubmitButton = styled.button`
   cursor:${p=>p.disabled?"no-allowed":"pointer"};
   opacity:${p=>p.disabled?"0.5":"1"};
`;
const GRADE_LEVEL_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const DesctiptionArea = () => {
  return (
    <>
      <p>
        Today, you will take a Spanish placement assessment. This is not a test
        and won’t affect your grades. The purpose is to see what you already
        know in Spanish so we can place you in the right Spanish class when you
        start high school.
      </p>
      <p>
        This assessment helps us make sure you’re in a class where you can do
        your best and feel comfortable learning. Just relax and try your
        best—there’s nothing to worry about!
      </p>
      <p>
        Take your time, read each question carefully, and enjoy showing what you
        know. Your Spanish teacher is excited to help you succeed in high
        school!
      </p>
    </>
  );
};
const StudentPlacementForm =  forwardRef(({ onSubmit, sessionInfo , is_microphone_required , schools , checkMicorphone , micConfirmed , setMicModalVisible , has_question }, ref)  => {
  const showAlert = useAlert();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    student_id: "",
    school_name: "",
    grade_level: "",
  });
  const firebaseContex = useFirebase();
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    student_id: false,
    school_name: false,
    grade_level: false,
  });
  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setErrors({ ...errors, [field]: false });
    // Clear error on input change
  };

  const validateForm = () => {
    const newErrors = {
      firstName: !formData.firstName.trim(),
      lastName: !formData.lastName.trim(),
      student_id: !formData.student_id.trim()|| formData.student_id.trim().length<6,
      school_name: !formData.school_name.trim(),
      grade_level: !String(formData.grade_level || "").trim(),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error); // Return true if no errors
  };
  async function createSession() {
    setLoader(true);
    try {
      if(!formData.student_id||formData.student_id.trim().length<6){
        return showAlert(`Student ID must be at least six digits.`, 'error')
      }
      sessionStorage.removeItem("remainingTime");
      const resetResponse = await handleVerifyAuth('schoologyAuth', 'placement_exam', 'PlacementExam');
      if (resetResponse && resetResponse.success && resetResponse.data?.token) {
        const decoded = decodeJwtPayload(resetResponse.data.token);
        if (decoded && decoded.username && decoded.password) {
          const auth_config = authHelper({
            username: decoded.username,
            password: decoded.password,
          });
          const awsLogin = await handleAwsLogin(auth_config);
          if(awsLogin && awsLogin.isSignedIn){
            await firebaseContex.signInWithCustomTokenApi(decoded.username);
          }else{
            showAlert("Authentication failed. Please try login again!", 'error');
            return;
          }
        }
      }else{
        showAlert("Authentication failed. Please try login again!", 'error');
        return;
      }
      const { exam_info } = sessionInfo || {};
      if (!exam_info) return;
      const { assigned, assignment } = exam_info;
      let start_video_url = null;
      if (
        assignment &&
        assignment.questions &&
        Array.isArray(assignment.questions)
      ) {
        const firstSection = assignment.questions[0];
        if (firstSection && firstSection.video) {
          start_video_url = firstSection.video;
        }
      }
      const session_dt = await dispatch(
        handleCreatePlacementExamSession(
          {
            exam_code: assigned.assignment_code,
            user_info: {
              student_id: formData.student_id,
              first_name: formData.firstName,
              last_name: formData.lastName,
              school_name: formData.school_name,
              grade_level: formData.grade_level,
            },
          },
          start_video_url
        )
      );
      if (session_dt && session_dt.success) {
        onSubmit(session_dt.data);
        showAlert("Submitted successfully!", 'success')
      } else {
        showAlert("Something went wrong!", 'error')
      }
    } catch (error) {
      console.error(error, "Error in creating session");
      Sentry.captureException(`490 Error in createSession: ${error}`);
      showAlert("Something went wrong!", 'error');
    } finally {
      setLoader(false);
    }
  }

  const handleButtonClick = async () => {
    if (!validateForm()) {
      showAlert("Please fill all the fields", 'error');
      return;
    }
  
    if (is_microphone_required && !micConfirmed) {
      setMicModalVisible(true);
      return;
    }
  
    const micStatus = await checkMicorphone();
    if (!micStatus) return;
  
    createSession();
  };
  useImperativeHandle(ref, () => ({
    createSession,
  }));
  const placementInputFields = [
    { id: "firstName", label: "Student First Name", errorMsg: "First name is required." },
    { id: "lastName", label: "Student Last Name", errorMsg: "Last name is required." },
    { id: "student_id", label: "Student ID Number", errorMsg: "Student ID is required." },
    { id: "school_name", label: "School Name", errorMsg: "School name is required." },
    { id: "grade_level", label: "Grade Level", errorMsg: "Grade level is required." }
  ];
  const getMessage = (field) => {
    if (field && field.id) {
      if (field.id == 'student_id') {
        if (formData.student_id.trim().length < 6) {
          return `Student ID must be at least six digits.`;
        } else {
          return field.errorMsg
        }
      }
      return field.errorMsg
    }
    return ''
  }
  return (
    <SentryWrap>
      <SpinLoader dataloader={loader} />
      <div className="studentPlacementForm">
        <div className="mainStudentContainer">
          <div className="allStdntFormsection">
            <form className="placementExamStudentForm">
              {placementInputFields.map((field, index) => (
                <div className="studentFormGroup" key={field.id}>
                  <label htmlFor={field.id}>{field.label}</label>
                  {field.id === "school_name" ? (
                    <SchoolSelector
                      showSearch
                      id="school_name"
                      placeholder="Select a school"
                      value={formData.school_name || undefined}
                      onChange={(value) => handleInputChange("school_name", value)}
                      filterOption={(input, option) =>
                        (option?.children || "").toLowerCase().includes(input.toLowerCase())
                      }
                      style={{ width: "100%" }}
                    >
                      {schools.map((school) => (
                        <Select.Option key={school.id} value={school.displayName}>
                          {school.displayName}
                        </Select.Option>
                      ))}
                    </SchoolSelector>
                  ) : field.id === "grade_level" ? (
                    <SchoolSelector
                      id="grade_level"
                      placeholder="Select grade"
                      value={formData.grade_level || undefined}
                      onChange={(value) => handleInputChange("grade_level", value)}
                      style={{ width: "100%" }}
                    >
                      {GRADE_LEVEL_OPTIONS.map((g) => (
                        <Select.Option key={g} value={g}>
                          {g}
                        </Select.Option>
                      ))}
                    </SchoolSelector>
                  ) : (
                    <InputField
                      type="text"
                      id={field.id}
                      placeholder=""
                      value={formData[field.id]}
                      onChange={(e) => handleInputChange(field.id, e.target.value)}
                      error={errors[field.id]}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const nextField = document.getElementById(
                            placementInputFields[index + 1]?.id
                          );
                          if (nextField) {
                            nextField.focus();
                          }
                        }
                      }}
                    />
                  )}
                  {errors[field.id] && (
                    <ErrorMessage>{getMessage(field)}</ErrorMessage>
                  )}
                </div>
              ))}
            </form>
          </div>
        </div>
      </div>
      <div className="stdntGetStartedBtn">
        <button type="button" onClick={handleButtonClick}>
          {has_question?"Continue":"Get started"}
        </button>
      </div>
    </SentryWrap>
  );
});

const QuestionRunner = forwardRef(({ exam_config, sessionInfo, onComplete , is_microphone_required ,checkMicorphone ,micConfirmed ,setMicModalVisible },ref) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const dispatch = useDispatch();
  const questions = exam_config?.questions || [];
  const { session_info } = sessionInfo || {};
  const [loader, setLoader] = useState(false);
  const handleAnswer = (value) => {
    setResponses((prev) => ({
      ...prev,
      [currentIndex]: {
        question:current.question_text,
        answer:value
      },
    }));
  };
  const onSubmit = async () =>{
    try {
      setLoader(true)
      if (is_microphone_required && !micConfirmed) {
        setMicModalVisible(true);
        return;
      }
      const micStatus = await checkMicorphone();
      if (!micStatus) return;
      await dispatch(handleUpdatePlacementExamSession('session_respone', responses, session_info?.id , 'pre_response'))
      onComplete(responses);
    } catch (error) {
      
    } finally{
      setLoader(false)
    }
   
  }
  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    } else {
      onSubmit()
    }
  };
  useImperativeHandle(ref, () => ({
    onSubmit,
  }));
  const current = questions[currentIndex];

  const renderQuestion = () => {
    if (!current) return null;

    return (
      <>
        <SpinLoader dataloader={loader} />
        <div className="studentPlacementForm" style={{width:"auto"}}>
          <div className="mainStudentContainer">
            <div className="allStdntFormsection" style={{ minHeight:300 , minWidth:450}}>
              <div className="placementExamStudentForm">
                <Title color="#fff" level={4} style={{textAlign: "left",color: "#fff"}}>
                  {`Question ${currentIndex + 1}: ${current.question_text}`}
                </Title>

                {current.type === "multiple_choice" && (
                  <Radio.Group
                    onChange={(e) => handleAnswer(e.target.value)}
                    value={responses[currentIndex]?.answer}
                    style={{ display: "flex", flexDirection: "column", marginTop: 16 }}
                  >
                    {current.options.map((opt, idx) => (
                      <Radio key={idx} value={opt} style={{ color:"#fff"}}>
                        {opt}
                      </Radio>
                    ))}
                  </Radio.Group>
                )}

                {current.type === "open_response" && (
                  <TextArea
                    rows={4}
                    placeholder="Write your response here..."
                    value={responses[currentIndex]?.answer || ""}
                    onChange={(e) => handleAnswer(e.target.value)}
                    style={{ marginTop: 16, minHeight:"20vh" }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };
const has_no_answer = !Boolean((responses[currentIndex]?.answer || "").trim?.())
  return (
    <div style={{ marginTop: 24,width:'100%' }}>
      {renderQuestion()}
      <div className="stdntGetStartedBtn">
        <SubmitButton type="button" onClick={handleNext} disabled={has_no_answer}>
          {currentIndex === questions.length - 1 ? "Get started" : "Next"}
        </SubmitButton>
      </div>
    </div>
  );
});
const PlacementExamStudentForm = () => {
  const navigate = useNavigate();
  const showAlert = useAlert();
  const [isStarted, setIsStarted] = useState(false);
  const location = useLocation();
  const sessionInfo = usePlacementExamSession();
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();
  const schools = useMemo(()=>sessionInfo.exam_info?.schools || [],[sessionInfo])
  const exam_config = useMemo(() => sessionInfo.exam_info?.exam_config && sessionInfo.exam_info?.exam_config?.id ? sessionInfo.exam_info?.exam_config : null, [sessionInfo]);
  const has_question = useMemo(()=>exam_config&&exam_config.id&&Array.isArray(exam_config.questions)&&exam_config.questions.length>0,[exam_config])
  const [currentStep, setCurrentStep] = useState("init"); 
  const studentFormRef = useRef()
  const questionRef = useRef()
  const isTouchDevice = useTouchDetection();
  const is_microphone_required = useMemo(() => {
    const { exam_info } = sessionInfo || {};
    const { assigned, assignment, organization } = exam_info || {};
    if (assigned && assignment && assignment.questions && Array.isArray(assignment.questions)) {
      const all_question = assignment.questions.flatMap(k => k.questions)
      if (Array.isArray(all_question)) {
        return all_question.some(k=>k.type=="audio")
      }
      return false;
    }
    return false;
  }, [sessionInfo])

  const [micModalVisible, setMicModalVisible] = useState(false);
  const [micConfirmed, setMicConfirmed] = useState(false);
  const [micErrorModalVisible, setMicErrorModalVisible] = useState(false);
  const checkMicorphone = async () => {
    if (is_microphone_required) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((track) => track.stop());
        return true;
      } catch (err) {
        setMicErrorModalVisible(true);
        return false;
      }
    }
    return true;
  };
  useEffect(() => {
    if (!sessionInfo || !sessionInfo.exam_info) {
      navigate(-1);
    } else if (sessionInfo.session_info) {
      setIsStarted(true);
    }
  }, [sessionInfo]);

  useEffect(() => {
    if (!sessionInfo || !sessionInfo.exam_info) {
      navigate("/placement-exam");
    }
  }, [sessionInfo]);
  const onFormSubmit = (data) => {
    setIsStarted(true);
    if (Array.isArray(exam_config?.questions) && exam_config.questions.length > 0) {
      setCurrentStep("questions");
      navigate(`/placement-exam/form?tab=question&session_id=${data.id}`);
    } else {
      setCurrentStep("ready");
      navigate(`/placement-exam/form?tab=start_assignment&session_id=${data.id}`);
    }
  };
  const createSubmissions = async () => {
    try {
      setLoader(true);
      const { session_info, exam_info } = sessionInfo;
      const { assigned, assignment, organization } = exam_info;
      const { user_info , session_respone } = session_info;
      await dispatch(
        handleCreatePlacementExamSubmissions({
          session_id: sessionInfo?.session_info?.id || 0,
          assignment_code: assigned.assignment_code,
          answers: [],
          firstName: user_info.first_name,
          lastName: user_info.last_name,
          school_name: user_info.school_name,
          grade_level: user_info.grade_level,
          student_id: user_info.student_id,
          status: "started",
          organization_id: organization.id,
          assignment_id: assignment.id,
          session_respone:session_respone
        })
      );
      onAssignmentStart();
    } catch (error) {
      showAlert("Something went wrong!", 'error');
    } finally {
      setLoader(false);
    }
  };
  const onAssignmentStart = () => {
    if(!isTouchDevice){
       goFullScreen();
    }
    navigate(
      `/placement-exam/exam?session_id=${
        sessionInfo?.session_info?.id || 0
      }&section=0&question=0`
    );
  };
  const onConfirmMicrophone =async () => {
    setMicModalVisible(false);
    setMicConfirmed(true);
    try {
      const micStatus = await checkMicorphone();
    if (micStatus) {
       if(has_question){
        questionRef.current.onSubmit()
       }else{
        studentFormRef.current.createSession()
       }
    }
    } catch (error) {
      
    }
    
  }
  const dist_config = sessionInfo.exam_info?.config || {};
  return (
    <SentryWrap>
    <div className="placementExamformcontainer">
      <Modal
        open={micModalVisible}
        title="Microphone Permission Required"
        onOk={onConfirmMicrophone}
        onCancel={() => {
          setMicModalVisible(false);
        }}
        okText="Allow Microphone"
        cancelText="Cancel"
      >
        <p>
          This assessment includes questions that require microphone access. We need your permission to proceed.
        </p>
        <p>
          Please click "Allow Microphone" to grant access and start the assessment.
        </p>
      </Modal>
      <Modal
        className="studentQuestionModal"
        open={micErrorModalVisible}
        onCancel={() => setMicErrorModalVisible(false)}
        footer={null}
      >
        <div className="stdntModalTxt">
          <img src={PlacementExamStopIcon} alt="Mic Error" />
          <p>
            No se pudo acceder al micrófono.<br />
            Por favor, permite el acceso al micrófono<br />
            en la configuración del navegador para continuar.
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <button onClick={() => setMicErrorModalVisible(false)} className="next-button">
            Cerrar
          </button>
        </div>
      </Modal>
      <SpinLoader dataloader={loader} />
      {/* Header Section */}
      <header className="studentFormHeader">
        <div className="headerRowSection">
          <div className="StudentSecondLogo">
            <img src={placement_exam_logo} alt="Placement Exam Logo" />
          </div>
          <div className="studentFormLogo">
            {dist_config?.logo ? (
              <img src={dist_config?.logo} alt="Placement District Logo" />
            ) : null}
          </div>
        </div>
      </header>
      {currentStep === "init" && (
        <>
          <h1 className="studentFormTxt">
            {dist_config?.title ? dist_config?.title : "Welcome"}
          </h1>
          <StudentPlacementForm
            onSubmit={onFormSubmit}
            sessionInfo={sessionInfo}
            checkMicorphone={checkMicorphone}
            is_microphone_required={is_microphone_required && !has_question}
            setMicModalVisible={setMicModalVisible}
            ref={studentFormRef}
            micConfirmed={micConfirmed}
            has_question={has_question}
            schools={schools}
          />
        </>
      )}

      {currentStep === "questions" && (
        <QuestionRunner
          exam_config={exam_config}
          sessionInfo={sessionInfo}
          ref={questionRef}
          checkMicorphone={checkMicorphone}
          is_microphone_required={is_microphone_required}
          setMicModalVisible={setMicModalVisible}
          micConfirmed={micConfirmed}
          onComplete={(answers) => {
            setCurrentStep("ready");
          }}
        />
      )}

      {currentStep === "ready" && (
        <div>
          <div className="studentTxtSection">
            <h1 className="studentFormTxt">
              {dist_config?.title ? dist_config?.title : "Welcome"}
            </h1>
            {dist_config?.description ? (
              <p style={{ whiteSpace: "pre-line" }}>
                {dist_config?.description}
              </p>
            ) : (
              <DesctiptionArea />
            )}
            <h3>Good luck!</h3>
          </div>
          <div className="stdntGetStartedBtn">
            <button type="button" onClick={createSubmissions}>
              Start Assessment
            </button>
          </div>
        </div>
      )}
    </div>
    </SentryWrap>
  );
};

export default PlacementExamStudentForm;
